<div class="modal-header">
  <h4 class="modal-title qr-poster-title">Set up QR posters</h4>
  <button type="button" class="btn-close" aria-label="Close" title="Close Modal" (click)="close()"></button>
</div>

<div class="modal-body">
  <div class="data-showcase-grid">
    <p class="qr-poster-intro">
      A print-ready promo layer built around the QR code that opens your MiniMenu.
      Pick a size and layout, add your photo and words, then download the artwork.
    </p>

    <div class="row qr-poster-grid">
      <!-- Preview first: you read the result, then reach right for the controls. -->
      <div class="col-lg-5">
        <div class="qr-poster-preview">
          <div class="qr-poster-composite" style="--ar: 1.25" #composite>
            <div class="layer photo" [class.empty]="!image">
              <img *ngIf="image" [src]="image.src" alt="" />
            </div>
            <div class="layer art" #artHost></div>
          </div>
          <p class="qr-poster-preview-note">Poster preview &middot; {{ W }} &times; {{ H }}</p>
        </div>
      </div>

      <!-- One card per decision, so every control reads as part of its own section. -->
      <div class="col-lg-7">

        <section class="qr-card">
          <header class="qr-card-head">
            <h5>Poster size</h5>
            <p>The shape you are printing or posting.</p>
          </header>
          <div class="qr-seg" role="group" aria-label="Poster size">
            <button type="button" *ngFor="let c of canvases"
                    [attr.aria-pressed]="c.w === W && c.h === H"
                    [class.is-active]="c.w === W && c.h === H"
                    (click)="setCanvas(c)">{{ c.label }}</button>
          </div>
          <p class="qr-field-hint">Supply artwork at {{ W }} &times; {{ H }}.</p>
        </section>

        <section class="qr-card">
          <header class="qr-card-head">
            <h5>Layout</h5>
            <p>Where the words and the QR panel sit on the poster.</p>
          </header>
          <div class="qr-seg" role="group" aria-label="Layout">
            <button type="button" *ngFor="let id of availableLayouts"
                    [attr.aria-pressed]="layout === id"
                    [class.is-active]="layout === id"
                    (click)="setLayout(id)">{{ layoutMeta[id].title }}</button>
          </div>
          <p class="qr-field-hint">{{ layoutMeta[layout].hint }}</p>
        </section>

        <section class="qr-card">
          <header class="qr-card-head">
            <h5>Photo</h5>
            <p>Your picture sits under the overlay — it is not baked into the download.</p>
          </header>
          <div class="file-upload-wrap" *ngIf="!image">
            <input #bgInput type="file" accept="image/*" (change)="onPhotoSelected($event)"
                   (click)="bgInput.value = ''" />
            <div class="upload-inner">
              <img src="./assets/images/ic-add.svg" alt="Add Icon" class="add-icon" />
              <span>ADD PHOTO</span>
            </div>
          </div>
          <div class="qr-image-preview" *ngIf="image">
            <img [src]="image.src" alt="Poster photo" />
            <span class="close-span" title="Remove" (click)="clearPhoto()"></span>
          </div>
          <p class="qr-poster-msg" [class.is-ok]="imgMsgKind === 'ok'" [class.is-warn]="imgMsgKind === 'warn'">
            <b *ngIf="imgMsgDims">{{ imgMsgDims }}</b> {{ imgMsgText }}
          </p>
        </section>

        <section class="qr-card">
          <header class="qr-card-head">
            <h5>Ink</h5>
            <p>The colour of the type and hairlines over your photo.</p>
          </header>
          <div class="qr-seg" role="group" aria-label="Ink">
            <button type="button" [attr.aria-pressed]="ink === 'dark'" [class.is-active]="ink === 'dark'"
                    (click)="setInk('dark')">Light ink</button>
            <button type="button" [attr.aria-pressed]="ink === 'light'" [class.is-active]="ink === 'light'"
                    (click)="setInk('light')">Dark ink</button>
          </div>
          <p class="qr-field-hint">
            {{ ink === 'dark' ? 'Pale type and panel — made for darker photos.' : 'Dark type and a white panel — made for bright photos.' }}
          </p>
        </section>

        <section class="qr-card">
          <header class="qr-card-head">
            <h5>Business</h5>
            <p>Who the poster belongs to, and the link inside the QR code.</p>
          </header>
          <div class="row">
            <div class="col-sm-6">
              <label class="data-label" for="qrPosterBizName">Name</label>
              <input id="qrPosterBizName" type="text" class="form-control" [(ngModel)]="bizName"
                     [ngModelOptions]="{standalone: true}" (ngModelChange)="render()" />
            </div>
            <div class="col-sm-6">
              <label class="data-label" for="qrPosterBizDetail">Detail line</label>
              <input id="qrPosterBizDetail" type="text" class="form-control" [(ngModel)]="bizDetail"
                     [ngModelOptions]="{standalone: true}" placeholder="City · What you do"
                     (ngModelChange)="render()" />
            </div>
            <div class="col-12">
              <label class="data-label">Logo mark</label>
              <div class="file-upload-wrap qr-logo-tile" *ngIf="!logoImg">
                <input #logoInput type="file" accept="image/*" (change)="onLogoSelected($event)"
                       (click)="logoInput.value = ''" />
                <div class="upload-inner">
                  <img src="./assets/images/ic-add.svg" alt="Add Icon" class="add-icon" />
                  <span>ADD LOGO</span>
                </div>
              </div>
              <div class="qr-image-preview qr-logo-preview" *ngIf="logoImg">
                <img [src]="logoImg" alt="Logo mark" />
                <span class="close-span" title="Use initials instead" (click)="clearLogo()"></span>
              </div>
              <p class="qr-field-hint">Without a logo the poster shows your initials in a ring.</p>
            </div>
            <div class="col-12">
              <label class="data-label" for="qrPosterCtaUrl">Menu link — encoded into the QR</label>
              <input id="qrPosterCtaUrl" type="text" class="form-control" [(ngModel)]="ctaUrl"
                     [ngModelOptions]="{standalone: true}" (ngModelChange)="render()" />
              <p class="qr-field-hint">{{ qrNote }}</p>
            </div>
          </div>
        </section>

        <section class="qr-card">
          <header class="qr-card-head">
            <h5>Words</h5>
            <p>The offer, in your own voice.</p>
          </header>
          <div class="row">
            <div class="col-12">
              <label class="data-label" for="qrPosterEyebrow">Eyebrow</label>
              <input id="qrPosterEyebrow" type="text" class="form-control" [(ngModel)]="eyebrow"
                     [ngModelOptions]="{standalone: true}" (ngModelChange)="render()" />
            </div>
            <div class="col-12">
              <label class="data-label" for="qrPosterHeadline">Headline — one line per row</label>
              <textarea id="qrPosterHeadline" class="form-control textarea-cnt" rows="2"
                        [(ngModel)]="headline" [ngModelOptions]="{standalone: true}"
                        (ngModelChange)="render()"></textarea>
            </div>
          </div>
        </section>

        <section class="qr-card">
          <header class="qr-card-head">
            <h5>Download</h5>
            <p>Transparent SVG — type, hairlines, the QR panel and one soft gradient. Your photo is not baked in.</p>
          </header>
          <button type="button" class="btn btn-primary w-100 btn-lg" title="Download overlay (SVG)"
                  (click)="downloadSvg()">Download overlay (SVG)</button>
        </section>

      </div>
    </div>
  </div>
</div>
